/**
 * Pages resource hooks. Mirrors the API endpoint shape from
 * `api/src/pages/pages.routes.ts`:
 *
 *   - GET    /api/admin/sites/:siteId/pages — paginated list
 *   - POST   /api/admin/sites/:siteId/pages — create
 *   - GET    /api/admin/pages/:id           — page + versions
 *   - PATCH  /api/admin/pages/:id           — update
 *   - DELETE /api/admin/pages/:id           — soft delete
 *
 * Create / delete invalidate the site-scoped list. Slice 1 does not touch
 * the page-version editor — the versions in `PageWithVersionsResponse`
 * are surfaced read-only.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { CreatePageInput, PageResponse, PageWithVersionsResponse } from "@ottopilot/shared";
import { apiDelete, apiList, apiPost } from "../lib/api";
import type { ApiClientError, PageMeta } from "../lib/api";
import { queryKeys } from "./queryKeys";

export type PagesListResult = {
  data: PageResponse[];
  meta: PageMeta | undefined;
};

export function usePages(siteId: string | undefined, params: { page: number; pageSize: number }) {
  return useQuery<PagesListResult, ApiClientError>({
    queryKey: queryKeys.pages.listForSite(siteId ?? "", params),
    queryFn: () =>
      apiList<PageResponse[]>(`/api/admin/sites/${siteId ?? ""}/pages`, {
        query: { page: params.page, pageSize: params.pageSize },
      }),
    enabled: Boolean(siteId),
    placeholderData: keepPreviousData,
  });
}

export function useCreatePage(siteId: string) {
  const qc = useQueryClient();
  return useMutation<PageWithVersionsResponse, ApiClientError, CreatePageInput>({
    mutationFn: (input) =>
      apiPost<PageWithVersionsResponse>(`/api/admin/sites/${siteId}/pages`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.all });
    },
  });
}

export function useDeletePage() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { id: string }>({
    mutationFn: ({ id }) => apiDelete<void>(`/api/admin/pages/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pages.all });
    },
  });
}
